import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { ArrowLeft, Edit, MapPin, Calendar, User } from 'lucide-react';
import GoogleMap from '../components/GoogleMap';
import { useAuth } from '../contexts/AuthContext';

const FestivalView = () => {
  const { festivalId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch festival data
  const { data: festivalData, isLoading, error } = useQuery(
    ['festival', festivalId],
    async () => {
      const response = await axios.get(`/api/festivals/${festivalId}`);
      return response.data;
    }
  );

  if (isLoading) {
    return (
      <div className="loading" style={{ padding: '40px', textAlign: 'center' }}>
        <div>Loading festival...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Error Loading Festival</h2>
        <p>{error.response?.data?.error || 'Failed to load festival'}</p>
        <button
          onClick={() => navigate('/admin/festivals')}
          className="btn btn-primary"
        >
          Back to Festivals
        </button>
      </div>
    );
  }

  const festival = festivalData?.festival;

  if (!festival) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Festival Not Found</h2>
        <button
          onClick={() => navigate('/admin/festivals')}
          className="btn btn-primary"
        >
          Back to Festivals
        </button>
      </div>
    );
  }

  const canEdit = user && festival.creator_id === user.id;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button
            onClick={() => navigate('/admin/festivals')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <ArrowLeft size={16} />
            Back to Festivals
          </button>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '600' }}>
            {festival.name}
          </h1>
          <span style={{
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '500',
            background: festival.status === 'published' ? '#d4edda' : 
                       festival.status === 'draft' ? '#fff3cd' : '#f8d7da',
            color: festival.status === 'published' ? '#155724' : 
                   festival.status === 'draft' ? '#856404' : '#721c24'
          }}>
            {festival.status.charAt(0).toUpperCase() + festival.status.slice(1)}
          </span>
        </div>
        
        {canEdit && (
          <Link
            to={`/admin/festivals/${festival.id}/edit`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: '#007bff',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <Edit size={16} />
            Edit Festival
          </Link>
        )}
      </div>

      {/* Festival Details */}
      <div style={{ display: 'grid', gap: '30px' }}>
        {/* Basic Information */}
        <div style={{
          background: 'white',
          padding: '30px',
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}>
          <h2 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '600' }}>
            Festival Information
          </h2>
          
          <div style={{ display: 'grid', gap: '20px' }}>
            {festival.description && (
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                  Description
                </h3>
                <p style={{ color: '#666', lineHeight: '1.6' }}>
                  {festival.description}
                </p>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Calendar size={20} style={{ color: '#007bff' }} />
                <div>
                  <div style={{ fontSize: '14px', color: '#666' }}>Start Date</div>
                  <div style={{ fontWeight: '500' }}>
                    {new Date(festival.start_date).toLocaleString()}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Calendar size={20} style={{ color: '#007bff' }} />
                <div>
                  <div style={{ fontSize: '14px', color: '#666' }}>End Date</div>
                  <div style={{ fontWeight: '500' }}>
                    {new Date(festival.end_date).toLocaleString()}
                  </div>
                </div>
              </div>

              {festival.location_name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <MapPin size={20} style={{ color: '#007bff' }} />
                  <div>
                    <div style={{ fontSize: '14px', color: '#666' }}>Location</div>
                    <div style={{ fontWeight: '500' }}>
                      {festival.location_name}
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <User size={20} style={{ color: '#007bff' }} />
                <div>
                  <div style={{ fontSize: '14px', color: '#666' }}>Created</div>
                  <div style={{ fontWeight: '500' }}>
                    {new Date(festival.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Location Map */}
        {festival.location_lat && festival.location_lng && (
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <h2 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '600' }}>
              Festival Location
            </h2>
            
            <div style={{ 
              border: '1px solid #e0e0e0', 
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <GoogleMap
                initialLat={festival.location_lat}
                initialLng={festival.location_lng}
                initialZoom={festival.zoom_level || 15}
                height="400px"
                onLocationSelect={() => {}} // Read-only map
              />
            </div>
            
            {festival.location_name && (
              <p style={{ 
                marginTop: '15px', 
                fontSize: '14px', 
                color: '#666',
                textAlign: 'center'
              }}>
                📍 {festival.location_name}
              </p>
            )}
          </div>
        )}

        {/* Additional Actions */}
        <div style={{
          background: 'white',
          padding: '30px',
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}>
          <h2 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '600' }}>
            Festival Management
          </h2>
          
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <Link
              to={`/admin/festivals/${festival.id}/editor`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: '#28a745',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <MapPin size={16} />
              Manage POIs
            </Link>

            {canEdit && (
              <Link
                to={`/admin/festivals/${festival.id}/edit`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#007bff',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <Edit size={16} />
                Edit Details
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FestivalView;
